import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSnackbar } from 'redux/snackbar';
import api from 'services/api';
import { useLocation } from 'react-router-dom';
import { filterMessageError } from './helpers';
import { Chip } from '@mui/material';
import PhysicalPondsService from 'services/PhysicalPondsService';
import { POND_STATUS } from 'utils/consts';

export function usePhysicalPonds() {
  const [physicalPonds, setPhysicalPonds] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [openProgress, setOpenProgress] = useState(false);
  const [errors, setErrors] = useState([]);
  const [errorsWhenPosting, setErrorsWhenPosting] = useState([]);

  const location = useLocation();
  const pond = location?.state?.pond;
  const dispatch = useDispatch();

  const columns = [
    {
      title: 'Nome',
      field: 'description',
      hideFilterIcon: true,
      customSort: (a, b) => a.description.length - b.description.length,
    },
    {
      title: 'Área',
      field: 'capacity',
      render: (rowData) => `${new Intl.NumberFormat('pt-BR').format(rowData.capacity)} m²`,
      hideFilterIcon: true,
    },
    {
      title: 'Status',
      field: 'status',
      editable: 'never',
      render: (rowData) => (rowData.status === POND_STATUS.MANUTENCAO ? (
        <Chip
          style={{
            backgroundColor: '#F44336',
            color: '#fff',
            fontWeight: 'bold',
          }}
          label={rowData.status}
        />
      ) : (
        <Chip
          style={{
            backgroundColor: '#008836',
            color: '#fff',
            fontWeight: 'bold',
          }}
          label={rowData.status}
        />
      )),
      hideFilterIcon: true,
      lookup: {
        'Em tratamento': 'Em tratamento',
        Povoado: 'Povoado',
        inativo: 'inativo',
        Outro: 'Outro',
      },
    },
    {
      title: 'Tipo',
      field: 'type',
      editable: 'never',
      hideFilterIcon: true,
    },
    {
      title: 'Sistema',
      field: 'system',
      editable: 'never',
      hideFilterIcon: true,
    },
    {
      title: 'Protocolo',
      field: 'protocol',
      editable: 'never',
      hideFilterIcon: true,
    },
  ];

  const columnsForExport = [
    {
      title: 'Nome',
      field: 'description',
    },
    {
      title: 'Status do viveiro',
      field: 'status',
    },
    {
      title: 'Tipo',
      field: 'type',
    },
    {
      title: 'Área',
      field: 'capacity',
    },
    {
      title: 'Profundidade',
      field: 'depth',
    },
    {
      title: 'Sistema de produção',
      field: 'system',
    },
    {
      title: 'Protocolo',
      field: 'protocol',
    },
  ];

  useEffect(() => {
    async function loadData() {
      try {
        setOpenProgress(false);
        const response = await api.get('/ponds');
        if (response.data) {
          const data = await response.data;
          setOpenProgress(true);
          setPhysicalPonds(data);
        }
      } catch (err) {
        setErrors((prev) => [...prev, err.message]);
      } finally {
        setOpenProgress(true);
      }
    }

    loadData();
  }, []);

  useEffect(() => {
    async function loadInputsExport() {
      try {
        const response = await (await api.get('/ponds/export')).data;

        if (response.length === 0) {
          setOpenProgress(true);
        }
        if (response) {
          setExportData(response);
        }
      } catch (err) {
        setErrors((prev) => [err.message, ...prev]);
        setOpenProgress(true);
      }
    }
    loadInputsExport();
  }, []);

  // eslint-disable-next-line consistent-return
  const handleImportDataPost = async (payload) => {
    if (!payload) throw new Error('Payload is required.');
    try {
      setOpenProgress(false);
      const result = await PhysicalPondsService.importData(payload);
      const {
        errorsData, importedData, status, successMessage,
      } = result;

      if (status === 201) {
        if (importedData.length === 0) {
          if (errorsData.length > 0) {
            return setErrorsWhenPosting(filterMessageError(errorsData));
          }
          return dispatch(
            setSnackbar(
              true,
              'error',
              'Erro ao importar planilha! Verifique se existe dados.',
            ),
          );
        }
        if (importedData.length > 0) {
          const updatedList = await PhysicalPondsService.pondsList();
          setPhysicalPonds(updatedList);
          dispatch(
            setSnackbar(
              true,
              'success',
              successMessage,
            ),
          );
          if (errorsData.length > 0) {
            setErrorsWhenPosting(filterMessageError(errorsData));
          }
          setOpenProgress(true);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Ocorreu um erro ao inserir novos dados.';
      dispatch(
        setSnackbar(
          true,
          'error',
          errorMessage,
        ),
      );
      throw new Error(errorMessage);
    }
  };

  const handleCloseSnackbar = () => {
    setErrorsWhenPosting([]);
  };

  return {
    physicalPonds,
    exportData,
    openProgress,
    columns,
    columnsForExport,
    errors,
    errorsWhenPosting,
    handleCloseSnackbar,
    handleImportDataPost,
    pond,
  };
}
